import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { getNewsletterStats } from 'helpers/http/common';
import toast from 'react-hot-toast';
import moment from 'moment';
import {
  X,
  Mail,
  MailOpen,
  MousePointerClick,
  AlertCircle,
  Shield,
  UserX,
} from 'lucide-react';
import Loader from 'components/Loader';

interface NewsletterAnalyticsProps {
  newsletterId: string;
  onClose: () => void;
}

const NewsletterAnalytics: React.FC<NewsletterAnalyticsProps> = ({
  newsletterId,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [newsletter, setNewsletter] = useState<any>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNewsletterStats(newsletterId);
      setStats(data.stats);
      setNewsletter(data.newsletter);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [newsletterId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: any;
    color: string;
  }) => (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle p-3 me-3"
            style={{ backgroundColor: `${color}20`, color }}
          >
            <Icon size={24} />
          </div>
          <div className="flex-grow-1">
            <div className="text-muted small">{title}</div>
            <div className="fs-4 fw-bold" style={{ color }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            {subtitle && <div className="text-muted small">{subtitle}</div>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return <Loader />;
  }

  if (!stats || !newsletter) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No analytics data available.</p>
        <button className="btn btn-secondary mt-2" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h5 className="mb-1 fw-600">{newsletter.subject}</h5>
          <small className="text-muted">
            Sent:{' '}
            {newsletter.sent_at
              ? moment(newsletter.sent_at).format('MMM DD, YYYY HH:mm')
              : 'N/A'}
          </small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <Row>
        <Col md={6}>
          <StatCard
            title="Total Sent"
            value={newsletter.sent_count}
            subtitle={`${newsletter.recipient_count} recipients`}
            icon={Mail}
            color="#0d6efd"
          />
        </Col>
        <Col md={6}>
          <StatCard
            title="Delivered"
            value={stats.delivered}
            subtitle={`${(
              (stats.delivered / newsletter.sent_count) *
              100
            ).toFixed(1)}% delivery rate`}
            icon={Mail}
            color="#198754"
          />
        </Col>
        <Col md={6}>
          <StatCard
            title="Opened"
            value={stats.unique_opens}
            subtitle={`${stats.opened} total opens • ${
              stats.rates?.open_rate || 0
            }% open rate`}
            icon={MailOpen}
            color="#ffc107"
          />
        </Col>
        <Col md={6}>
          <StatCard
            title="Clicked"
            value={stats.unique_clicks}
            subtitle={`${stats.clicked} total clicks • ${
              stats.rates?.click_rate || 0
            }% click rate`}
            icon={MousePointerClick}
            color="#fd7e14"
          />
        </Col>
        <Col md={6}>
          <StatCard
            title="Bounced"
            value={stats.bounced}
            subtitle={`${stats.rates?.bounce_rate || 0}% bounce rate`}
            icon={AlertCircle}
            color="#dc3545"
          />
        </Col>
        <Col md={6}>
          <StatCard
            title="Unsubscribed"
            value={stats.unsubscribed}
            subtitle="Users who unsubscribed"
            icon={UserX}
            color="#6c757d"
          />
        </Col>
      </Row>

      {stats.spam_reports > 0 && (
        <Card className="mt-3 border-warning">
          <Card.Body>
            <div className="d-flex align-items-center">
              <Shield size={20} className="text-warning me-2" />
              <strong>Spam Reports:</strong>
              <Badge bg="warning" className="ms-2">
                {stats.spam_reports}
              </Badge>
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="mt-4">
        <h6 className="mb-3">Event Breakdown</h6>
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Event Type</th>
                <th>Total Count</th>
                <th>Unique Count</th>
              </tr>
            </thead>
            <tbody>
              {stats.event_breakdown?.map((event: any) => (
                <tr key={event.event_type}>
                  <td>
                    <Badge bg="secondary">{event.event_type}</Badge>
                  </td>
                  <td>{event.count.toLocaleString()}</td>
                  <td>{event.unique_count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsletterAnalytics;
